/**
 * Pack fulfillment notes — theme cart + UpCart.
 * Stores note as _ships_as (hidden). Renders via UpCart-native span classes.
 */

const PACK_NOTE_CLASS = 'cart-item__pack-note';
const PACK_NOTE_ATTR = 'data-pack-note-key';
const PACK_NOTE_PROPERTY = '_ships_as';
const PACK_BASE_PROPERTY = '_pack_base_sticks';
const UPCART_NOTE_CLASS =
  'upcart-internal-cart-items__key-value-pair upcart-public-cart-items__key-value-pair';

let packNoteSyncing = false;

function buildPackNote(quantity, baseSticks) {
  const qty = parseInt(quantity, 10);
  const sticks = parseInt(baseSticks, 10);
  if (!qty || qty <= 1 || !sticks) return '';
  const totalPk = qty * sticks;
  return `${totalPk} pk comes as ${qty} x ${sticks}ct pouches`;
}

function getBaseSticksFromItem(item) {
  const props = item.properties || {};
  const fromProp = parseInt(props[PACK_BASE_PROPERTY] || props['_pack_base_sticks'], 10);
  if (fromProp) return fromProp;

  const shipsAs = props[PACK_NOTE_PROPERTY] || props._ships_as || props['Ships as'] || '';
  const match = shipsAs.match(/x\s*(\d+)ct\s*pouches/i);
  return match ? parseInt(match[1], 10) : 0;
}

function getPackNoteFromItem(item) {
  const baseSticks = getBaseSticksFromItem(item);
  if (baseSticks) return buildPackNote(item.quantity, baseSticks);

  const props = item.properties || {};
  return props[PACK_NOTE_PROPERTY] || props._ships_as || props['Ships as'] || '';
}

function isUpcartActive() {
  return typeof window.upcartDocumentOrShadowRoot !== 'undefined';
}

function getUpcartRoot() {
  return window.upcartDocumentOrShadowRoot || null;
}

const NATIVE_PACK_NOTE_PATTERN = /ships\s*as|_ships_as/i;

function hideNativePackNoteRows(tile) {
  const wrap = tile.querySelector('.upcart-public-component-product-tile__properties');
  if (!wrap) return;

  wrap.querySelectorAll('.upcart-public-cart-items__key-value-pair').forEach((row) => {
    if (row.hasAttribute(PACK_NOTE_ATTR)) return;
    if (NATIVE_PACK_NOTE_PATTERN.test(row.textContent || '')) {
      row.style.display = 'none';
    }
  });
}

function ensureUpcartPackNoteElement(tile, note, itemKey) {
  const wrap = tile.querySelector('.upcart-public-component-product-tile__properties');
  if (!wrap) return;

  hideNativePackNoteRows(tile);

  let el = wrap.querySelector(`[${PACK_NOTE_ATTR}="${itemKey}"]`);

  if (!note) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement('span');
    el.className = UPCART_NOTE_CLASS;
    el.setAttribute(PACK_NOTE_ATTR, itemKey);
    wrap.appendChild(el);
  }

  el.textContent = note;
  el.style.display = '';
}

function formatUpcartPackNoteDisplay(cart) {
  const root = getUpcartRoot();
  if (!root || !cart?.items?.length) return;

  const tiles = root.querySelectorAll('.upcart-public-component-product-tile');

  cart.items.forEach((item, index) => {
    const note = getPackNoteFromItem(item);
    const tile = tiles[index];
    if (!tile) return;
    ensureUpcartPackNoteElement(tile, note, item.key);
  });
}

function injectPackNote(container, note, itemKey) {
  if (!container || !note) return;

  const existing = container.querySelector(`[${PACK_NOTE_ATTR}="${itemKey}"]`);
  if (existing) {
    existing.textContent = note;
    return;
  }

  const p = document.createElement('p');
  p.className = PACK_NOTE_CLASS;
  p.setAttribute(PACK_NOTE_ATTR, itemKey);
  p.textContent = note;
  container.appendChild(p);
}

function pruneStalePackNotes(activeKeys) {
  document.querySelectorAll(`.${PACK_NOTE_CLASS}[${PACK_NOTE_ATTR}]`).forEach((el) => {
    const key = el.getAttribute(PACK_NOTE_ATTR);
    if (!activeKeys.has(key)) el.remove();
  });
}

function findThemeLineItemContainer(item, index) {
  const selectors = [
    `#CartItem-${index + 1} .cart-item__details`,
    `#cart-notification-product-${item.key}`,
    `[data-cart-item-key="${item.key}"]`,
    `[data-line-key="${item.key}"]`,
    `[data-line-item-key="${item.key}"]`,
  ];

  for (const selector of selectors) {
    const el = document.querySelector(selector);
    if (el) return el;
  }

  return null;
}

function decorateThemeCartPackNotes(cart) {
  if (isUpcartActive()) return;

  const items = cart?.items;
  if (!items?.length) return;

  const activeKeys = new Set();
  items.forEach((item, index) => {
    const note = getPackNoteFromItem(item);
    if (!note) return;
    activeKeys.add(item.key);
    const container = findThemeLineItemContainer(item, index);
    injectPackNote(container, note, item.key);
  });

  pruneStalePackNotes(activeKeys);
}

function lineItemPropertiesForSync(item, expectedNote, baseSticks) {
  const props = { ...(item.properties || {}) };
  delete props['Ships as'];

  if (expectedNote) {
    props[PACK_NOTE_PROPERTY] = expectedNote;
    props[PACK_BASE_PROPERTY] = String(baseSticks);
  } else {
    delete props[PACK_NOTE_PROPERTY];
    delete props._ships_as;
  }
  return props;
}

async function syncUpcartPackNotes(cart) {
  if (packNoteSyncing || !cart?.items?.length) return;

  const updates = [];
  for (const item of cart.items) {
    const baseSticks = getBaseSticksFromItem(item);
    if (!baseSticks) continue;

    const expected = buildPackNote(item.quantity, baseSticks);
    const current =
      item.properties?.[PACK_NOTE_PROPERTY] || item.properties?.['Ships as'] || '';
    if (expected === current) continue;

    updates.push({
      id: item.key,
      quantity: item.quantity,
      properties: lineItemPropertiesForSync(item, expected, baseSticks),
    });
  }

  if (!updates.length) return;

  packNoteSyncing = true;
  try {
    for (const update of updates) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(update),
      });
      if (!response.ok) throw new Error(`cart/change failed: ${response.status}`);
    }
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
    }
  } catch (err) {
    console.error('cart-pack-note sync:', err);
  } finally {
    packNoteSyncing = false;
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const syncUpcartPackNotesDebounced = debounce(syncUpcartPackNotes, 300);
const formatUpcartDebounced = debounce((cart) => formatUpcartPackNoteDisplay(cart), 50);

function runUpcartFormat(cart) {
  formatUpcartPackNoteDisplay(cart);
  requestAnimationFrame(() => formatUpcartPackNoteDisplay(cart));
}

function handleUpcartPackNotes(cart) {
  runUpcartFormat(cart);
  syncUpcartPackNotesDebounced(cart);
}

function handleCartPackNotes(cart) {
  if (!cart?.items) return;
  if (isUpcartActive()) {
    handleUpcartPackNotes(cart);
    return;
  }
  decorateThemeCartPackNotes(cart);
}

function fetchAndHandleCartPackNotes() {
  fetch(`${window.routes?.cart_url || '/cart'}.js`)
    .then((response) => response.json())
    .then(handleCartPackNotes)
    .catch((err) => console.error('cart-pack-note:', err));
}

const handleDebounced = debounce(fetchAndHandleCartPackNotes, 200);

function observeUpcartDom() {
  const root = getUpcartRoot();
  if (!root || root.__packNoteObserved) return;
  root.__packNoteObserved = true;

  const observer = new MutationObserver(() => {
    const cart = typeof window.upcartGetCart === 'function' ? window.upcartGetCart() : null;
    if (cart?.items) formatUpcartDebounced(cart);
  });

  observer.observe(root, { childList: true, subtree: true });
}

function initUpcartPackNoteSync() {
  if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;

  const onCart = (event) => handleUpcartPackNotes(event.cart);

  window.upcartSubscribeCartLoaded(onCart);
  window.upcartSubscribeCartUpdated(onCart);

  if (typeof window.upcartSubscribeAddedToCart === 'function') {
    window.upcartSubscribeAddedToCart(() => {
      const cart = window.upcartGetCart?.();
      if (cart) runUpcartFormat(cart);
    });
  }

  if (typeof window.upcartSubscribeCartOpened === 'function') {
    window.upcartSubscribeCartOpened(() => {
      const cart = window.upcartGetCart?.();
      if (cart) runUpcartFormat(cart);
    });
  }

  observeUpcartDom();

  if (typeof window.upcartGetCart === 'function') {
    const cart = window.upcartGetCart();
    if (cart?.items) handleUpcartPackNotes(cart);
  }
  return true;
}

function waitForUpcart(attemptsLeft) {
  if (initUpcartPackNoteSync()) return;
  if (attemptsLeft <= 0) return;
  setTimeout(() => waitForUpcart(attemptsLeft - 1), 250);
}

window.buildPackFulfillmentNote = buildPackNote;

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, handleDebounced);
}

document.addEventListener('DOMContentLoaded', () => {
  waitForUpcart(40);
  if (!isUpcartActive()) fetchAndHandleCartPackNotes();
});
